import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, X, Check, CheckCheck, ExternalLink } from 'lucide-react';
import { getUserNotifications, markNotificationRead, markAllNotificationsRead, getUnreadNotificationCount } from '../../services/notificationService';
import { toDisplayText } from '../../utils/displayText';
import './NotificationDropdown.css';

const NotificationDropdown = ({ userId }) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const authFailedRef = useRef(false);
  const isRTL = i18n.language === 'ar';

  // Fetch notifications and unread count
  const fetchNotifications = async () => {
    if (!userId) return;

    // Skip if auth previously failed (polling already stopped)
    if (authFailedRef.current) {
      return;
    }
    
    try {
      setLoading(true);
      const [notificationsData, count] = await Promise.allSettled([
        getUserNotifications({ limit: 20, unread_only: false }),
        getUnreadNotificationCount()
      ]);
      
      // Check for authentication errors in results
      let hasAuthError = false;
      
      if (notificationsData.status === 'rejected') {
        const error = notificationsData.reason;
        const isAuthError = error?.isAuthError || 
                           error?.status === 401 || 
                           error?.status === 403 ||
                           error?.message?.includes('401') || 
                           error?.message?.includes('403') ||
                           error?.message?.includes('Unauthorized') ||
                           error?.message?.includes('expired') ||
                           error?.message?.includes('Invalid token');
        if (isAuthError) {
          hasAuthError = true;
          console.warn('Authentication error fetching notifications:', error);
        } else {
          console.error('Error fetching notifications:', error);
        }
      } else {
        setNotifications(notificationsData.value.notifications || []);
      }
      
      if (count.status === 'rejected') {
        const error = count.reason;
        const isAuthError = error?.isAuthError || 
                           error?.status === 401 || 
                           error?.status === 403 ||
                           error?.message?.includes('401') || 
                           error?.message?.includes('403') ||
                           error?.message?.includes('Unauthorized') ||
                           error?.message?.includes('expired') ||
                           error?.message?.includes('Invalid token');
        if (isAuthError) {
          hasAuthError = true;
          console.warn('Authentication error fetching unread count:', error);
        } else {
          console.error('Error fetching unread count:', error);
        }
      } else {
        setUnreadCount(count.value || 0);
      }
      
      // Stop polling if any auth error occurred
      if (hasAuthError) {
        authFailedRef.current = true;
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        console.warn('Authentication failed, stopping notification polling');
      }
    } catch (error) {
      console.error('Unexpected error in fetchNotifications:', error);
      
      // Check if it's an authentication error
      const isAuthError = error?.isAuthError || 
                         error?.status === 401 || 
                         error?.status === 403 ||
                         error?.message?.includes('401') || 
                         error?.message?.includes('403') ||
                         error?.message?.includes('Unauthorized') ||
                         error?.message?.includes('expired') ||
                         error?.message?.includes('Invalid token');
      
      if (isAuthError) {
        // Stop polling if token is expired/invalid
        authFailedRef.current = true;
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        console.warn('Authentication failed, stopping notification polling');
      }
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch — runs once on mount to populate the unread badge
  useEffect(() => {
    if (userId) {
      fetchNotifications();
    }
  }, [userId]);

  // Poll only while the dropdown is open AND the tab is visible
  useEffect(() => {
    if (!isOpen || !userId) return;

    // Fetch immediately when dropdown opens
    fetchNotifications();

    const startPolling = () => {
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = setInterval(fetchNotifications, 30000);
    };

    const stopPolling = () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };

    // Pause polling when the tab is hidden, resume when visible
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        fetchNotifications(); // refresh immediately on tab re-focus
        startPolling();
      }
    };

    startPolling();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isOpen, userId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleMarkAsRead = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await markNotificationRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true, read_at: new Date().toISOString() } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async (e) => {
    e.stopPropagation();
    try {
      await markAllNotificationsRead();
      setNotifications(prev => 
        prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const lang = i18n.language && i18n.language.startsWith('ar') ? 'ar' : 'en';
  const getNotificationMessage = (notification) => {
    const title = toDisplayText(notification.title_en ?? notification.title_ar, lang) || toDisplayText(notification.title_ar ?? notification.title_en, lang) || '';
    const message = toDisplayText(notification.message_en ?? notification.message_ar, lang) || toDisplayText(notification.message_ar ?? notification.message_en, lang) || '';
    return { title, message };
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('notifications.justNow', 'Just now');
    if (diffMins < 60) return t('notifications.minutesAgo', { count: diffMins }, `${diffMins}m ago`);
    if (diffHours < 24) return t('notifications.hoursAgo', { count: diffHours }, `${diffHours}h ago`);
    if (diffDays < 7) return t('notifications.daysAgo', { count: diffDays }, `${diffDays}d ago`);
    return date.toLocaleDateString(i18n.language === 'ar' ? 'ar-SA-u-nu-latn' : 'en-US', { month: 'short', day: 'numeric' });
  };

  const handleNotificationClick = (notification) => {
    // Mark as read if unread
    if (!notification.is_read) {
      handleMarkAsRead(notification.id, { stopPropagation: () => {} });
    }
    
    // Navigate to assessment if it's an assessment notification
    if (notification.entity_type === 'assessment' && notification.entity_id) {
      window.location.href = `/assessments/${notification.entity_id}`;
    }
  };

  return (
    <div className="notification-dropdown-container" ref={dropdownRef}>
      <button
        className="notification-bell-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t('common.notifications', 'Notifications')}
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className={`notification-dropdown ${isRTL ? 'rtl' : 'ltr'}`}>
          <div className="notification-dropdown-header">
            <h3>{t('notifications.title', 'Notifications')}</h3>
            {unreadCount > 0 && (
              <button
                className="mark-all-read-btn"
                onClick={handleMarkAllAsRead}
                title={t('notifications.markAllRead', 'Mark all as read')}
              >
                <CheckCheck size={16} />
                <span>{t('notifications.markAllRead', 'Mark all as read')}</span>
              </button>
            )}
            <button
              className="close-dropdown-btn"
              onClick={() => setIsOpen(false)}
              aria-label={t('common.close', 'Close')}
            >
              <X size={18} />
            </button>
          </div>

          <div className="notification-list">
            {loading ? (
              <div className="notification-loading">
                {t('common.loading', 'Loading...')}
              </div>
            ) : notifications.length === 0 ? (
              <div className="notification-empty">
                {t('notifications.noNotifications', 'No notifications')}
              </div>
            ) : (
              notifications.map(notification => {
                const { title, message } = getNotificationMessage(notification);
                return (
                  <div
                    key={notification.id}
                    className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="notification-content">
                      <div className="notification-title">{title}</div>
                      <div className="notification-message">{message}</div>
                      <div className="notification-time">{formatDate(notification.created_at)}</div>
                    </div>
                    {!notification.is_read && (
                      <button
                        className="mark-read-btn"
                        onClick={(e) => handleMarkAsRead(notification.id, e)}
                        title={t('notifications.markAsRead', 'Mark as read')}
                      >
                        <Check size={16} />
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;

